import { Type } from 'class-transformer';
import {
  IsArray,
  IsDate,
  IsEnum,
  IsInt,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  ValidateNested,
} from 'class-validator';
import { MicronutrientResponseDto } from './micronutrient-response.dto';
import {
  NUTRITIONAL_PROFILE_METHODS,
  type NutritionalProfileMethod,
} from './nutritional-profile-method.enum';

/**
 * One stored version of a per-100g nutritional profile. Append-only: every
 * field is immutable after insert; a new version is a new row that supersedes
 * the previous one (`supersededById`). Decimal columns serialize as `number`
 * at the repository read boundary, consistent with the rest of the codebase.
 *
 * `supersededById` is null on the active version and points at the replacing
 * row on every superseded one.
 */
export class NutritionalProfileResponseDto {
  @IsUUID()
  id!: string;

  @IsOptional()
  @IsUUID()
  tenantId?: string | null;

  @IsUUID()
  ingredientId!: string;

  @IsInt()
  baseQuantityG!: number;

  @IsNumber()
  energyKj!: number;

  @IsNumber()
  energyKcal!: number;

  @IsNumber()
  fat!: number;

  @IsNumber()
  saturatedFat!: number;

  @IsNumber()
  carbohydrate!: number;

  @IsNumber()
  sugars!: number;

  @IsOptional()
  @IsNumber()
  fiber?: number | null;

  @IsNumber()
  protein!: number;

  @IsNumber()
  salt!: number;

  @IsOptional()
  @IsNumber()
  sodium?: number | null;

  @IsOptional()
  @IsNumber()
  alcohol?: number | null;

  @IsOptional()
  @IsNumber()
  polyols?: number | null;

  @IsOptional()
  @IsNumber()
  organicAcids?: number | null;

  /** All. VII Parte B punto 17: rapporto collagene/proteine di carne (%). */
  @IsOptional()
  @IsNumber()
  collagenToMeatProteinRatioPercent?: number | null;

  @IsEnum(NUTRITIONAL_PROFILE_METHODS)
  method!: NutritionalProfileMethod;

  @IsOptional()
  @IsUUID()
  sourceDocumentId?: string | null;

  @IsOptional()
  @IsString()
  sourceReference?: string | null;

  @IsOptional()
  @IsUUID()
  createdById?: string | null;

  @IsString()
  createdByName!: string;

  @IsOptional()
  @IsUUID()
  supersededById?: string | null;

  @IsDate()
  createdAt!: Date;

  /** Vitamine dichiarate su questa versione del profilo (Allegato XIII). */
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => MicronutrientResponseDto)
  micronutrients!: MicronutrientResponseDto[];
}
